import type { Nullable } from '@libs/core';
import type { Asset, Entry } from 'contentful';
import { ErrorBoundary } from '@libs/common-next';
import { isValidElement, type ReactNode } from 'react';
import { BuildContextProvider } from '../providers/BuildContextProvider';
import { singleEntryContentType } from '../types';
import type { PropsType } from '../types';
import { ComponentResolver } from './ComponentResolver';
import { componentMapping } from './componentMapping';
import { createUniqueIDs } from './createUniqueIDs';

const doesComponentExist = (child: Entry | Asset): boolean => {
    if (child?.sys?.type === 'Asset') {
        return false;
    }

    const typeName = child?.sys?.contentType?.sys?.id;

    return !!componentMapping[typeName];
};

/**
 * Returns a boolean if entry type is one of the type that requires to return one entry only
 * @param {Entry} entry - Contentful entry
 */
const isSingleEntryContentType = (entry: Entry) => {
    const typeName = entry?.sys?.contentType?.sys?.id;
    const singleEntryContentTypes: string[] = Object.values(singleEntryContentType);
    return singleEntryContentTypes.includes(typeName);
};

/**
 * Returns a boolean if the entry is the latest published entry
 * @param {Entry} entry - Contentful entry
 * @param {Map<string, Date>} entryMap - Map of entry type name and recent published entry date
 */
const isLatestEntry = (entryMap: Map<string, Date> | null, entry: Entry) => {
    if (isSingleEntryContentType(entry)) {
        const typeName = entry?.sys?.contentType?.sys?.id;
        const publishedEntryDate = new Date(entry?.sys?.updatedAt)?.getTime();
        const latestUpdateAt = entryMap?.get(typeName)?.getTime();
        return publishedEntryDate === latestUpdateAt;
    }

    return true;
};

/**
 * Returns a hashmap of the type name and recent published entry date
 * @param {Entry[]} entries - List of Contentful entries
 */
const getEntryDateMap = (entries: Entry[]) => {
    const map = new Map<string, Date>();
    for (const entry of entries) {
        const typeName = entry?.sys?.contentType?.sys?.id;
        const updateDate = (entry?.sys?.updatedAt && new Date(entry.sys.updatedAt)) || null;

        if (!typeName || !updateDate) {
            continue;
        }

        const mapDate = map.has(typeName) ? map.get(typeName) : null;
        if (!map.has(typeName) || (mapDate && updateDate > mapDate)) {
            map.set(typeName, updateDate);
        }
    }

    return map;
};

const filterChild = (child: ReactNode | PropsType) => {
    if (child !== null) {
        return typeof child === 'object' && !isValidElement(child) ? Object.values(child).every(v => v !== null) : true;
    }
    return false;
};

export const buildComponent = (component: Entry, componentKey?: string | number): ReactNode | PropsType => {
    if (!component?.fields) {
        return null;
    }

    const entries = Object.entries(component.fields).map(([key, value]) => {
        if (Array.isArray(value) && value[0]?.sys !== undefined) {
            const childrenComponents: Entry[] = createUniqueIDs(value);
            const branch = childrenComponents.map((child, index) => buildComponent(child, index)).filter(filterChild);

            return [key, branch];
        }

        if (value?.sys !== undefined) {
            return [key, buildComponent(value)];
        }

        return [key, value];
    });

    const props = {
        ...component,
        fields: Object.fromEntries(entries),
    };
    return doesComponentExist(props) ? (
        <ErrorBoundary key={componentKey} tags={{ contentful: 'true' }}>
            <ComponentResolver entry={props} />
        </ErrorBoundary>
    ) : (
        props.fields
    );
};

export const buildPage = (items: Entry[], pageId?: Nullable<string>) => {
    const mapEntries = items ? getEntryDateMap(items) : null;
    const builtComponents = items?.filter(entry => isLatestEntry(mapEntries, entry))?.map(buildComponent) || [];

    return <BuildContextProvider pageId={pageId}>{builtComponents.filter(isValidElement)}</BuildContextProvider>;
};
